import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { flyerAPI } from "api/Interactions/FlyersAPI";
import { Flyer } from "api/Interactions/InteractionsInterfaces";
import FlyerEmptyPage from "./FlyersEmptyPage";
import GenericDetailPage from "components/Interactions/GenericDetailPage";

interface FlyerDetailProps {
  flyerId: number;
  onClose?: () => void;
}

const FlyerDetail: React.FC<FlyerDetailProps> = ({ flyerId, onClose }) => {
  const theme = useTheme();
  const [record, setRecord] = useState<Flyer | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const numberOfFlyers = (record as any)?.people?.length || 0;
  const costPerFlyer = 0.05;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await flyerAPI.getById(flyerId);
        setRecord(data as Flyer);
      } catch (err) {
        console.error("Error loading flyer:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [flyerId]);

  const handleUpdate = async (id: number, field: keyof Flyer, value: any) => {
    if (typeof id !== "number") return;
    try {
      setSaving(true);
      // flyerAPI.updateField expects (id, fieldName, value)
      const updated = await flyerAPI.updateField(id, String(field), value);
      setRecord(updated as Flyer);
    } catch (err) {
      console.error(`Error updating field "${String(field)}":`, err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFulfilled = async () => {
    if (!record) return;
    await handleUpdate(
      record.id,
      "pathways_fulfilled" as keyof Flyer,
      !(record as any).pathways_fulfilled
    );
  };

  if (loading) return null;
  if (!record) return <FlyerEmptyPage />;

  return (
    <GenericDetailPage
      record={record}
      loading={loading}
      saving={saving}
      onClose={onClose}
      onUpdate={handleUpdate}
      onToggleFulfilled={handleToggleFulfilled}
      fulfilled={!!(record as any).pathways_fulfilled}
      fulfillmentDetailsLabel="Flyers"
      numberOfItems={numberOfFlyers}
      costPerItem={costPerFlyer}
      showSaveButton
      saveButtonColor={theme.custom.modules.interactions.primary.dark}
      fulfilledByPathwaysAvailable={false}
      registration={false}
      contentDetailFields={[
        {
          label: "File (Front)",
          value: (record as any).file_front,
          field: "file_front",
          type: "file",
          accept: "image/*,.pdf",
        },
        {
          label: "File (Back)",
          value: (record as any).file_back,
          field: "file_back",
          type: "file",
          accept: "image/*,.pdf",
        },
        {
          label: "Size",
          value: (record as any).size,
          field: "size",
          type: "select",
          options: [
            { label: "A6", value: "A6" },
            { label: "A5", value: "A5" },
            { label: "A4", value: "A4" },
          ],
        },

        // Stock Type (coating/stock)
        {
          label: "Paper Stock",
          value: (record as any).paper_type,
          field: "paper_type",
          type: "select",
          options: [
            { label: "Silk", value: "silk" },
            { label: "Gloss", value: "gloss" },
            { label: "Uncoated", value: "uncoated" },
            { label: "Recycled", value: "recycled" },
          ],
        },

        // Paper Weight (GSM)
        {
          label: "Paper Weight",
          value: (record as any).paper_weight,
          field: "paper_weight",
          type: "select",
          options: [
            { label: "130gsm", value: "130gsm" },
            { label: "170gsm", value: "170gsm" },
            { label: "250gsm", value: "250gsm" },
            { label: "300gsm", value: "300gsm" },
            { label: "350gsm", value: "350gsm" },
          ],
        },

        // Finish / Lamination
        {
          label: "Finish",
          value: (record as any).paper_finish,
          field: "paper_finish",
          type: "select",
          options: [
            { label: "No Lamination", value: "none" },
            { label: "Matt Lamination", value: "matt_lam" },
            { label: "Gloss Lamination", value: "gloss_lam" },
            { label: "Soft Touch Lamination", value: "soft_touch" },
            { label: "Spot UV", value: "spot_uv" },
          ],
        },

        {
          label: "Quantity to Print",
          value: (record as any).quantity,
          field: "quantity",
          type: "integer",
        },
      ]}
    />
  );
};

export default FlyerDetail;
